/*
 * vmcm-calculator.js
 * Smart wrap calculator for the MCM PDP (the live theme). The pure
 * recommendation math (recommend / better / panelView) started as a port of
 * the retired Dawn theme's velven-wrap-calculator.js but has since diverged:
 * a fixed 5 cm back return (not 3 cm), a stripsFor() seam overlap guard the
 * Dawn version never had, and a whole back model split into a wrapped edge
 * piece plus a separate centre fill piece (Dawn's whole back was one bigger
 * sheet folding in from all four edges: 2*W+2*T). Do not assume the two stay
 * in sync; verify against THIS file's own Node tests, not the Dawn one.
 *
 * The DOM controller below is rebuilt for the MCM theme: there is NO Dawn
 * product form here (no variant-selects, no quantity field, no #AddToCart), so
 * the buy button does its own cart safe cart/add.js write with the recommended
 * metres as the quantity, reading prices and variant ids from
 * window.VELVEN_MATERIALS (the same map the selection sheet uses).
 *
 * Best fit chooses the roll width automatically, while the customer can compare a
 * specific available 60, 90 or 120 cm roll. Math: every edge gets the panel
 * thickness plus a fixed 5 cm back return; grain runs along the roll length, so the
 * chosen grain locks the cut orientation. Automatic selection: fewest visible
 * front joins, then the fewest physical pieces and lengthwise trims, then cost.
 */
(function (global) {
  'use strict';

  // This asset can be requested by both a page section and the immersive
  // theme shell. Initialise once so a future template-shell upgrade cannot
  // duplicate the saved-plan bar, listeners or analytics events.
  if (global.VelvenOrderPlan) return;

  var BACK_RETURN = 5;  // cm back return per edge
  var TRIM = 5;         // cm cut allowance added to every strip's length, always
  var SEAM_OVERLAP = 4; // cm minimum overlap reserved per interior seam for a double cut
  var MIN_FINAL_WIDTH = 15; // cm: rebalance the final two strips below this handling width
  var MAX_PANEL_THICKNESS = 5; // cm: thicker panels need a manual plan from the Velven team
  var ROLLS = [60, 90, 120];

  // Guards the exact roll width boundary: ceil() alone can leave zero slack for a
  // double cut seam when `across` lands on an exact multiple of R. Bump the strip
  // count until every interior join has at least SEAM_OVERLAP cm of overlap slack.
  function stripsFor(across, R) {
    var strips = Math.max(1, Math.ceil(across / R));
    while (strips > 1 && (strips * R - across) < (strips - 1) * SEAM_OVERLAP) strips++;
    return strips;
  }

  // Pure. Turns a strip count into the physical cut plan a customer can follow.
  // Use the full roll width first and trim only the final remainder. Every join is
  // laid with SEAM_OVERLAP cm of raw overlap; seamPositions sit at the centre of
  // that overlap, where the double cut is made. If the remainder would be a fragile
  // sliver, width is borrowed from only the preceding strip so both stay practical.
  function cutPlan(across, R, overlap, minimumFinalWidth) {
    overlap = overlap == null ? SEAM_OVERLAP : Math.max(0, overlap);
    minimumFinalWidth = minimumFinalWidth == null ? MIN_FINAL_WIDTH : Math.max(0, minimumFinalWidth);
    if (!(across > 0) || !(R > 0) || !isFinite(across) || !isFinite(R)) {
      return { stripWidths: [], placementStarts: [], seamPositions: [], lengthwiseTrimCount: 0, rebalancedFinalStrip: false };
    }

    var strips = stripsFor(across, R);
    var rawWidth = across + Math.max(0, strips - 1) * overlap;
    var widths = [];
    for (var i = 0; i < strips - 1; i++) widths.push(R);
    widths.push(rawWidth - (strips - 1) * R);

    var rebalanced = false;
    if (strips > 1 && widths[strips - 1] < minimumFinalWidth) {
      var borrow = minimumFinalWidth - widths[strips - 1];
      widths[strips - 2] -= borrow;
      widths[strips - 1] += borrow;
      rebalanced = true;
    }

    var starts = [0];
    var seams = [];
    for (var j = 1; j < strips; j++) {
      starts.push(starts[j - 1] + widths[j - 1] - overlap);
      seams.push(starts[j] + overlap / 2);
    }
    var trims = widths.filter(function (width) { return width < R - 1e-9; }).length;
    return {
      stripWidths: widths,
      placementStarts: starts,
      seamPositions: seams,
      lengthwiseTrimCount: trims,
      rebalancedFinalStrip: rebalanced
    };
  }

  // Pure. o = {
  //   W, H, T,
  //   grain: 'up'|'across'|'auto',
  //   prices: { 60: n, 90: n, 120: n },
  //   back:  { mode: 'partial'|'full' }   // optional; default partial at BACK_RETURN
  // }
  // Returns the single best buy, or null if the face is not measured yet.
  function recommend(o) {
    var W = o.W, H = o.H, T = o.T == null ? 0 : Number(o.T);
    // isFinite also rejects Infinity: W/H > 0 alone lets it through (Infinity > 0 is
    // true), which would otherwise carry through to an Infinity priced, Infinity
    // metre "recommendation". The DOM controller already sanitizes its own inputs
    // before calling this, but recommend() is called directly elsewhere (tests, any
    // future caller) and should not trust its input either.
    if (!(W > 0) || !(H > 0) || !isFinite(W) || !isFinite(H) || !isFinite(T) || T < 0 || T > MAX_PANEL_THICKNESS) return null;
    var prices = o.prices || { 60: 25.95, 90: 34.95, 120: 49.95 };

    var back = o.back || {};
    var mode = back.mode === 'full' ? 'full' : 'partial';
    var depth = BACK_RETURN;

    // Edge piece: face plus thickness plus the fixed back return, on every edge.
    // Identical for both modes -- whole back wraps the edges exactly like "just the
    // edges" does; the only difference is whether a separate fill piece is also
    // needed for the remaining gap in the back's centre (see below).
    var Pw = W + 2 * (T + depth);
    var Ph = H + 2 * (T + depth);

    // Fill piece: covers whatever the edge piece's return does not reach on the
    // back. Only relevant in full mode. When the panel is small enough that the
    // returns from opposite edges already meet or overlap, there is nothing left to
    // fill -- whole back then reduces to exactly the edge piece, matching partial
    // mode's result for the same W, H, T.
    var hasFill = mode === 'full' && (W - 2 * depth) > 0 && (H - 2 * depth) > 0;
    var fillW = hasFill ? (W - 2 * depth) : 0;
    var fillH = hasFill ? (H - 2 * depth) : 0;

    var orients;
    if (o.grain === 'up') {
      orients = [{ eAcross: Pw, eLength: Ph, fAcross: fillW, fLength: fillH, grain: 'up' }];
    } else if (o.grain === 'across') {
      orients = [{ eAcross: Ph, eLength: Pw, fAcross: fillH, fLength: fillW, grain: 'across' }];
    } else {
      orients = [
        { eAcross: Pw, eLength: Ph, fAcross: fillW, fLength: fillH, grain: 'up' },
        { eAcross: Ph, eLength: Pw, fAcross: fillH, fLength: fillW, grain: 'across' }
      ];
    }

    var rolls = Array.isArray(o.availableRolls)
      ? ROLLS.filter(function (R) { return o.availableRolls.indexOf(R) !== -1; })
      : ROLLS;
    if (!rolls.length) return null;

    var best = null;
    for (var i = 0; i < orients.length; i++) {
      for (var r = 0; r < rolls.length; r++) {
        var R = rolls[r];
        var o1 = orients[i];
        var edgeStrips = stripsFor(o1.eAcross, R);
        var fillStrips = hasFill ? stripsFor(o1.fAcross, R) : 0;
        var edgeCutPlan = cutPlan(o1.eAcross, R);
        var fillCutPlan = hasFill ? cutPlan(o1.fAcross, R) : cutPlan(0, R);
        // TRIM guarantees excess to cut, applied to every strip cut off the roll --
        // the fill piece's strips are real, separate cuts too.
        var metres = (edgeStrips * (o1.eLength + TRIM) + fillStrips * (o1.fLength + TRIM)) / 100;
        var joins = edgeStrips - 1; // front facing seams only; the fill piece's own
                                     // seam(s) are tracked separately, not folded in
        var fillJoins = Math.max(0, fillStrips - 1);
        var totalPieces = edgeStrips + fillStrips;
        var lengthwiseTrimCount = edgeCutPlan.lengthwiseTrimCount + fillCutPlan.lengthwiseTrimCount;
        var buyM = Math.max(1, Math.ceil(metres - 1e-9)); // whole metres, guard fp overshoot
        var cost = buyM * prices[R];
        var cand = {
          roll: R, strips: edgeStrips, metres: metres, joins: joins,
          buyM: buyM, cost: cost, grain: o1.grain,
          mode: mode, depth: depth, pieceW: Pw, pieceH: Ph,
          fillStrips: fillStrips, fillJoins: fillJoins, fillW: fillW, fillH: fillH,
          edgeCutPlan: edgeCutPlan, fillCutPlan: fillCutPlan,
          totalPieces: totalPieces, lengthwiseTrimCount: lengthwiseTrimCount
        };
        if (best === null || better(cand, best)) best = cand;
      }
    }
    return best;
  }

  // Installation first: visible front joins, then total pieces/crosscuts, then
  // lengthwise trims and back-fill joins. Price only breaks equally simple plans.
  function better(a, b) {
    if (a.joins !== b.joins) return a.joins < b.joins;
    if (a.totalPieces !== b.totalPieces) return a.totalPieces < b.totalPieces;
    if (a.lengthwiseTrimCount !== b.lengthwiseTrimCount) return a.lengthwiseTrimCount < b.lengthwiseTrimCount;
    if (a.fillJoins !== b.fillJoins) return a.fillJoins < b.fillJoins;
    if (Math.abs(a.cost - b.cost) > 1e-9) return a.cost < b.cost;
    return a.buyM < b.buyM;
  }

  // Pure. Derives the diagram's logic and its plain-language caption.
  // o = { W, H, T, grain, directional, back: { mode } }
  function panelView(o) {
    var W = o.W, H = o.H;
    if (!(W > 0) || !(H > 0)) {
      return { placeholder: true, caption: 'Add width and height to picture your wrap.' };
    }
    var directional = o.directional !== false;
    var grain = directional ? (o.grain === 'across' ? 'across' : 'up') : 'none';
    var back = o.back || {};
    var mode = back.mode === 'full' ? 'full' : 'partial';
    var depth = BACK_RETURN;

    var grainPhrase = grain === 'across' ? 'Grain runs side to side.'
                    : grain === 'up' ? 'Grain runs up and down.'
                    : 'Smooth, non directional finish.';
    var backPhrase = mode === 'full'
      ? 'Wrap covers the whole back.'
      : 'Includes a 5 cm back return.';

    return {
      placeholder: false,
      grain: grain,
      backMode: mode,
      depthCm: depth,
      caption: grainPhrase + ' ' + backPhrase
    };
  }

  // Pure. The grain flip hint shown next to the recommendation, comparing the current
  // pick against the other grain direction's own recommend() result. Only claims one
  // piece with no join when switching grain would remove every join.
  function grainHint(rec, otherGrain, otherRec) {
    if (!rec || !otherRec || !(otherRec.joins < rec.joins)) return '';
    if (otherRec.joins === 0) {
      return otherGrain === 'up'
        ? 'Running the grain up lets you cover the surface with one piece and no join.'
        : 'Running the grain side to side lets you cover the surface with one piece and no join.';
    }
    return otherGrain === 'up'
      ? 'Running the grain up here means fewer joins.'
      : 'Running the grain side to side here means fewer joins.';
  }

  // Pure. The result panel's join summary line. Unchanged in meaning from before the
  // whole back redesign (still driven by rec.joins, the front facing seam count
  // only); when the recommendation also carries a fill piece for the back, one
  // honest extra sentence is appended regardless of the front join count, since the
  // fill piece's own seam is real but is not what "joins" here refers to.
  function joinsSummary(rec, isBestFit) {
    if (isBestFit == null) isBestFit = true;
    var base = rec.joins === 0
      ? 'The surface can be covered with one piece, so there should be no join.'
      : 'The surface will use ' + rec.strips + ' pieces with ' + rec.joins +
        (rec.joins === 1 ? ' join' : ' joins') +
        (isBestFit ? '. The ' + rec.roll + ' cm roll keeps joins to a minimum.' : '.');
    if (rec.fillStrips === 1) return base + ' The centre of the back uses one separate piece.';
    if (rec.fillStrips > 1) return base + ' The centre of the back uses ' + rec.fillStrips +
      ' strips with ' + rec.fillJoins + (rec.fillJoins === 1 ? ' join.' : ' joins.');
    return base;
  }

  function recommendationDisplay(rec, isBestFit) {
    if (!rec) return { heading: '', summary: '', buyLabel: '', showOversizedShipping: false };
    return {
      heading: 'You need ' + rec.buyM + ' m of ' + rec.roll + ' cm \u00b7 ' + money(rec.cost),
      summary: joinsSummary(rec, isBestFit),
      buyLabel: 'Add ' + rec.roll + 'cm x ' + rec.buyM + 'm roll to cart',
      showOversizedShipping: rec.roll === 120
    };
  }

  function rounded(n, places) {
    var factor = Math.pow(10, places == null ? 2 : places);
    return Math.round((Number(n) + Number.EPSILON) * factor) / factor;
  }

  // Stable rather than random so adding the same saved configuration again can
  // merge into the same Shopify line. Raw project dimensions stay in browser
  // storage and are deliberately excluded from cart properties and analytics.
  function orderPlanId(o) {
    var rec = o.rec || {};
    var source = [
      o.handle || '', o.variantId || 0, rec.roll || 0, rec.buyM || 0,
      rec.grain || o.grain || '', o.backMode || '', o.rollChoice || '',
      rec.joins || 0, rec.fillJoins || 0
    ].join('|');
    var hash = 2166136261;
    for (var i = 0; i < source.length; i++) {
      hash ^= source.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return 'vp1_' + (hash >>> 0).toString(36);
  }

  function createOrderPlan(o) {
    if (!o || !o.rec || !o.handle || !o.variantId) return null;
    var rec = o.rec;
    var now = Number(o.now) || Date.now();
    var directional = o.directional !== false;
    var orientation = directional
      ? (rec.grain === 'across' ? 'Side to side' : 'Up and down')
      : 'No fixed direction';
    var isBestFit = o.rollChoice === 'auto';
    return {
      schema: 1,
      experiment: 'saved_order_plan_v1',
      id: orderPlanId(o),
      savedAt: now,
      expiresAt: now + 7 * 24 * 60 * 60 * 1000,
      handle: String(o.handle),
      productName: String(o.productName || o.handle),
      productUrl: String(o.productUrl || ('/products/' + o.handle)),
      variantId: Number(o.variantId),
      widthCm: rounded(o.W, 2),
      heightCm: rounded(o.H, 2),
      thicknessCm: rounded(o.T, 2),
      grain: directional ? rec.grain : 'none',
      orientation: orientation,
      backMode: o.backMode === 'full' ? 'full' : 'partial',
      backLabel: o.backMode === 'full' ? 'Full back' : 'Edges only',
      rollChoice: String(o.rollChoice || 'auto'),
      rollWidthCm: rec.roll,
      recommendedMetres: rec.buyM,
      materialPrice: rounded(rec.cost, 2),
      materialAreaM2: rounded(rec.roll * rec.buyM / 100, 2),
      frontJoins: rec.joins,
      backJoins: rec.fillJoins,
      reasonCode: isBestFit ? 'best_fit' : 'selected_width',
      reasonText: isBestFit
        ? 'We selected this roll width to minimise joins, then the number of pieces and material cost.'
        : 'This result uses the roll width you selected.',
      allowanceText: 'Includes material for wrapping the edges, a 5 cm trimming allowance for each cut and 4 cm of overlap at each join.'
    };
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { recommend: recommend, better: better, panelView: panelView, FOLD_DEFAULT: BACK_RETURN, BACK_RETURN: BACK_RETURN, TRIM: TRIM, SEAM_OVERLAP: SEAM_OVERLAP, MIN_FINAL_WIDTH: MIN_FINAL_WIDTH, MAX_PANEL_THICKNESS: MAX_PANEL_THICKNESS, stripsFor: stripsFor, cutPlan: cutPlan, grainHint: grainHint, joinsSummary: joinsSummary, recommendationDisplay: recommendationDisplay, orderPlanId: orderPlanId, createOrderPlan: createOrderPlan };
    return;
  }

  // ---------- Browser DOM controller (MCM) ----------
  function money(n) { return '$' + n.toFixed(2); }
  function num(el) { var v = el ? parseFloat(el.value) : 0; return (isFinite(v) && v > 0) ? v : 0; }
  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function lerp(p, q, f) { return [p[0] + (q[0] - p[0]) * f, p[1] + (q[1] - p[1]) * f]; }
  function ptStr(arr) { return arr.map(function (p) { return p[0].toFixed(1) + ',' + p[1].toFixed(1); }).join(' '); }
  function setPoints(svg, sel, arr) { var el = svg.querySelector(sel); if (el) el.setAttribute('points', ptStr(arr)); }
  function setText(svg, sel, x, y, text) { var el = svg.querySelector(sel); if (!el) return; el.setAttribute('x', x.toFixed(1)); el.setAttribute('y', y.toFixed(1)); el.textContent = text; }

  function routes() { return (global.theme && global.theme.routes) || {}; }
  function addUrl() { return routes().cart_add_url || (routes().root_url || '/') + 'cart/add.js'; }

  var PLAN_STORAGE_KEY = 'velven:order-plan:v1';
  var PLAN_DISMISS_KEY = 'velven:order-plan-dismissed:v1';
  var lastPlanBarViewId = '';

  function storage(name) {
    try {
      var s = global[name];
      if (!s) return null;
      var probe = '__velven_plan_probe__';
      s.setItem(probe, '1');
      s.removeItem(probe);
      return s;
    } catch (e) { return null; }
  }

  function readOrderPlan() {
    var s = storage('localStorage');
    if (!s) return null;
    try {
      var plan = JSON.parse(s.getItem(PLAN_STORAGE_KEY) || 'null');
      if (!plan || plan.schema !== 1 || plan.experiment !== 'saved_order_plan_v1' ||
          !plan.id || !plan.handle || !plan.variantId || !(plan.expiresAt > Date.now())) {
        if (plan) s.removeItem(PLAN_STORAGE_KEY);
        return null;
      }
      return plan;
    } catch (e) {
      try { s.removeItem(PLAN_STORAGE_KEY); } catch (ignore) {}
      return null;
    }
  }

  function writeOrderPlan(plan) {
    var s = storage('localStorage');
    if (!s || !plan) return false;
    try {
      s.setItem(PLAN_STORAGE_KEY, JSON.stringify(plan));
      if (typeof global.CustomEvent === 'function') {
        global.document.dispatchEvent(new global.CustomEvent('velven:order-plan-changed', { detail: { id: plan.id } }));
      }
      return true;
    } catch (e) { return false; }
  }

  function clearOrderPlan() {
    var s = storage('localStorage');
    if (!s) return;
    try { s.removeItem(PLAN_STORAGE_KEY); } catch (e) {}
    if (typeof global.CustomEvent === 'function') global.document.dispatchEvent(new global.CustomEvent('velven:order-plan-changed'));
  }

  // Only allow the documented, privacy-safe fields into the data layer. Surface
  // width, height and thickness never pass this boundary.
  function trackOrderPlan(name, data) {
    var allowed = [
      'product_handle', 'variant_id', 'placement', 'config_id', 'roll_width_cm',
      'recommended_metres', 'selected_metres', 'previous_metres', 'orientation',
      'back_coverage', 'front_joins', 'back_joins', 'allowance_included',
      'reason_code', 'restored_via', 'replaced_existing'
    ];
    var event = { event: name, schema_version: 1, experiment: 'saved_order_plan_v1' };
    data = data || {};
    for (var i = 0; i < allowed.length; i++) {
      var key = allowed[i];
      if (data[key] !== undefined && data[key] !== null && data[key] !== '') event[key] = data[key];
    }
    global.dataLayer = global.dataLayer || [];
    global.dataLayer.push(event);
    if (typeof global.CustomEvent === 'function') {
      global.document.dispatchEvent(new global.CustomEvent('velven:calculator-event', { detail: { name: name, properties: data } }));
    }
  }

  function planEventData(plan, extra) {
    var data = {
      product_handle: plan.handle,
      variant_id: plan.variantId,
      config_id: plan.id,
      roll_width_cm: plan.rollWidthCm,
      recommended_metres: plan.recommendedMetres,
      orientation: plan.orientation,
      back_coverage: plan.backMode,
      front_joins: plan.frontJoins,
      back_joins: plan.backJoins,
      allowance_included: true,
      reason_code: plan.reasonCode
    };
    extra = extra || {};
    Object.keys(extra).forEach(function (key) { data[key] = extra[key]; });
    return data;
  }

  function sameProductPath(plan) {
    var path = global.location && global.location.pathname ? global.location.pathname.replace(/\/$/, '') : '';
    var target = (plan.productUrl || ('/products/' + plan.handle)).replace(/\/$/, '');
    return path === target;
  }

  function mountOrderPlanBar() {
    if (!global.document || !global.document.body) return;
    var old = global.document.querySelector('[data-order-plan-bar]');
    if (old) old.remove();
    global.document.body.classList.remove('has-vmcm-planbar');
    if (global.document.body.classList.contains('template-cart')) return;
    var plan = readOrderPlan();
    if (!plan) return;
    var session = storage('sessionStorage');
    if (session && session.getItem(PLAN_DISMISS_KEY) === plan.id) return;

    var bar = global.document.createElement('aside');
    bar.className = 'vmcm-planbar';
    bar.setAttribute('data-order-plan-bar', '');
    bar.setAttribute('aria-label', 'Saved order plan');

    var copy = global.document.createElement('div');
    copy.className = 'vmcm-planbar__copy';
    var label = global.document.createElement('span');
    label.className = 'vmcm-planbar__label';
    label.textContent = 'Order plan saved';
    var detail = global.document.createElement('span');
    detail.className = 'vmcm-planbar__detail';
    detail.textContent = plan.productName + ', ' + plan.rollWidthCm + ' cm x ' + plan.recommendedMetres + ' m';
    copy.appendChild(label);
    copy.appendChild(detail);

    var resume = global.document.createElement('a');
    resume.className = 'vmcm-planbar__resume';
    resume.textContent = 'Resume';
    resume.href = sameProductPath(plan)
      ? '#vmcm-pdp-buy'
      : (plan.productUrl || ('/products/' + plan.handle)) + '?vmcm_plan=' + encodeURIComponent(plan.id) + '#vmcm-pdp-buy';
    resume.addEventListener('click', function () {
      trackOrderPlan('order_plan_resume_clicked', planEventData(plan, { placement: 'resume_bar' }));
    });

    var close = global.document.createElement('button');
    close.className = 'vmcm-planbar__close';
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss saved order plan');
    close.textContent = '\u00d7';
    close.addEventListener('click', function () {
      trackOrderPlan('order_plan_bar_dismissed', planEventData(plan, { placement: 'resume_bar' }));
      if (session) session.setItem(PLAN_DISMISS_KEY, plan.id);
      bar.remove();
      global.document.body.classList.remove('has-vmcm-planbar');
    });

    bar.appendChild(copy);
    bar.appendChild(resume);
    bar.appendChild(close);
    global.document.body.appendChild(bar);
    global.document.body.classList.add('has-vmcm-planbar');
    if (lastPlanBarViewId !== plan.id) {
      trackOrderPlan('order_plan_bar_viewed', planEventData(plan, { placement: 'resume_bar' }));
      lastPlanBarViewId = plan.id;
    }
  }

  global.VelvenOrderPlan = {
    read: readOrderPlan,
    write: writeOrderPlan,
    clear: clearOrderPlan,
    track: trackOrderPlan,
    eventData: planEventData,
    refreshBar: mountOrderPlanBar,
    storageKey: PLAN_STORAGE_KEY
  };

  function materialFor(handle) {
    var list = global.VELVEN_MATERIALS || [];
    for (var i = 0; i < list.length; i++) { if (list[i].handle === handle) return list[i]; }
    return null;
  }

  function grainLinesSvg(face, dir) {
    if (dir === 'none') return '';
    var n = 8, s = '';
    for (var i = 1; i < n; i++) {
      var f = i / n, a, b;
      if (dir === 'up') { a = lerp(face.tl, face.tr, f); b = lerp(face.bl, face.br, f); }
      else { a = lerp(face.tl, face.bl, f); b = lerp(face.tr, face.br, f); }
      s += '<line x1="' + a[0].toFixed(1) + '" y1="' + a[1].toFixed(1) +
           '" x2="' + b[0].toFixed(1) + '" y2="' + b[1].toFixed(1) + '"/>';
    }
    return s;
  }

  // Seam lines where two strips meet. Fractions come from the selected physical
  // cut plan, so the lightweight SVG and the 3D view show the same real layout.
  function seamLinesSvg(face, dir, fractions) {
    if (!fractions || !fractions.length || dir === 'none') return '';
    var s = '';
    for (var i = 0; i < fractions.length; i++) {
      var f = fractions[i], a, b;
      if (dir === 'across') { a = lerp(face.tl, face.bl, f); b = lerp(face.tr, face.br, f); }
      else { a = lerp(face.tl, face.tr, f); b = lerp(face.bl, face.br, f); }
      s += '<line x1="' + a[0].toFixed(1) + '" y1="' + a[1].toFixed(1) +
           '" x2="' + b[0].toFixed(1) + '" y2="' + b[1].toFixed(1) + '"/>';
    }
    return s;
  }

  // Paint the isometric (cabinet projection) panel. view comes from panelView();
  // dims carries the raw cm values for scaling and labels, plus the recommended
  // strip count / cut orientation for the seams. faceView is 'front' | 'back'.
  function paintPanel(svg, view, dims, faceView) {
    if (!svg) return;
    var ph = svg.querySelector('[data-cal-ph]');
    var scene = svg.querySelector('[data-cal-scene]');
    var fig = svg.closest ? svg.closest('figure') : null;
    var cap = fig ? fig.querySelector('[data-cal-cap]') : null;
    if (cap) cap.textContent = view.caption;

    if (view.placeholder) {
      if (ph) ph.style.display = '';
      if (scene) scene.style.display = 'none';
      return;
    }
    if (ph) ph.style.display = 'none';
    if (scene) scene.style.display = '';

    var W = dims.W, H = dims.H, T = dims.T;
    var s = 200 / Math.max(W, H);                 // fit the longest side to 200 px
    var fw = clamp(W * s, 24, 240), fh = clamp(H * s, 24, 240);
    var dpx = clamp(T * 7 + 4, 6, 46);            // drawn thickness eases with T
    var dvx = dpx * 0.85, dvy = -dpx * 0.85;      // oblique depth vector, up and to the right
    var ox = 120, oy = 250;                        // face bottom left anchor
    var bl = [ox, oy], br = [ox + fw, oy], tr = [ox + fw, oy - fh], tl = [ox, oy - fh];
    var face = { tl: tl, tr: tr, br: br, bl: bl };
    function add(p) { return [p[0] + dvx, p[1] + dvy]; }

    setPoints(svg, '[data-cal-back-plane]', [add(tl), add(tr), add(br), add(bl)]);
    setPoints(svg, '[data-cal-top]', [tl, tr, add(tr), add(tl)]);
    setPoints(svg, '[data-cal-side]', [tr, br, add(br), add(tr)]);
    setPoints(svg, '[data-cal-face]', [tl, tr, br, bl]);

    var backFace = { tl: add(tl), tr: add(tr), br: add(br), bl: add(bl) };
    var isBack = faceView === 'back';
    var stripGrain = dims.stripGrain || view.grain;

    var g = svg.querySelector('[data-cal-grainlines]');
    if (g) g.innerHTML = grainLinesSvg(face, view.grain);

    // Seams sit on whichever surface is the wrap: the face in front view, and
    // the whole-back sheet when the customer flips to the back in full-cover
    // mode. In back + partial the back is bare (only the return lip is wrapped)
    // so no seams are drawn there.
    var seamG = svg.querySelector('[data-cal-seams]');
    if (seamG) {
      var showSeams = !isBack || view.backMode === 'full';
      var seamFractions = isBack ? dims.backSeamFractions : dims.frontSeamFractions;
      seamG.innerHTML = showSeams ? seamLinesSvg(isBack ? backFace : face, stripGrain, seamFractions) : '';
    }

    var back = svg.querySelector('[data-cal-back-plane]');
    var lip = svg.querySelector('[data-cal-lip]');
    var faceEl = svg.querySelector('[data-cal-face]');
    var topEl = svg.querySelector('[data-cal-top]');
    var sideEl = svg.querySelector('[data-cal-side]');

    // The partial-mode return lip is always positioned so the toggle can reveal it.
    var lipFrac = clamp(view.depthCm / 20, 0.06, 1) * 0.5; // portion of the top face the lip covers
    setPoints(svg, '[data-cal-lip]', [lerp(tl, add(tl), 1 - lipFrac), lerp(tr, add(tr), 1 - lipFrac), add(tr), add(tl)]);

    if (isBack) {
      // Look through the panel to the back: fade the front stack, raise the back.
      if (faceEl) faceEl.style.opacity = '0.1';
      if (topEl) topEl.style.opacity = '0.28';
      if (sideEl) sideEl.style.opacity = '0.28';
      if (g) g.style.opacity = '0';
      if (back) back.style.opacity = view.backMode === 'full' ? '1' : '0.3';
      if (lip) lip.style.opacity = view.backMode === 'full' ? '0' : '1';
    } else {
      if (faceEl) faceEl.style.opacity = '1';
      if (topEl) topEl.style.opacity = '';
      if (sideEl) sideEl.style.opacity = '';
      if (g) g.style.opacity = '';
      if (back) back.style.opacity = view.backMode === 'full' ? '1' : '0.18';
      if (lip) lip.style.opacity = view.backMode === 'full' ? '0' : '1';
    }

    setText(svg, '[data-cal-wlab]', ox + fw / 2, oy + 24, Math.round(W) + ' cm');
    setText(svg, '[data-cal-hlab]', ox - 12, oy - fh / 2, Math.round(H) + ' cm');
  }

  function svgSeamFractions(rec, W, H, backFace) {
    if (!rec) return [];
    var faceAcross = rec.grain === 'across' ? H : W;
    if (!(faceAcross > 0)) return [];
    var positions;
    if (backFace) {
      if (!(rec.fillStrips > 1)) return [];
      positions = rec.fillCutPlan.seamPositions.map(function (p) { return (BACK_RETURN + p) / faceAcross; });
    } else {
      var pieceAcross = rec.grain === 'across' ? rec.pieceH : rec.pieceW;
      var overhang = Math.max(0, (pieceAcross - faceAcross) / 2);
      positions = rec.edgeCutPlan.seamPositions.map(function (p) { return (p - overhang) / faceAcross; });
    }
    return positions.filter(function (f) { return f > 1e-6 && f < 1 - 1e-6; });
  }

  function init(root) {
    if (!root || root.dataset.bound === '1') return;
    root.dataset.bound = '1';

    var handle = root.dataset.vmcmCalHandle || '';
    var mat = materialFor(handle);
    function rollSize(roll) { return mat && mat.sizes ? mat.sizes['r' + roll] : null; }
    function rollPrice(roll) {
      var sz = rollSize(roll);
      var c = sz ? parseInt(sz.id, 10) && sz.price : 0; // price only trusted when the variant exists
      return (sz && parseInt(sz.id, 10) && c > 0) ? c / 100 : 0;
    }
    var prices = { 60: rollPrice(60), 90: rollPrice(90), 120: rollPrice(120) };
    var availableRolls = ROLLS.filter(function (roll) {
      var sz = rollSize(roll);
      return !!(sz && parseInt(sz.id, 10) && sz.available !== false && prices[roll] > 0);
    });
    var directional = root.dataset.vmcmCalDirectional !== 'false';

    var w = root.querySelector('[data-cal-w]');
    var h = root.querySelector('[data-cal-h]');
    var t = root.querySelector('[data-cal-t]');
    if (w) w.setAttribute('step', '1');
    if (h) h.setAttribute('step', '1');
    var grainWrap = root.querySelector('[data-cal-grain]');
    var grainBtns = Array.prototype.slice.call(root.querySelectorAll('[data-cal-grainbtn]'));
    var presetBtns = Array.prototype.slice.call(root.querySelectorAll('[data-cal-preset]'));
    var rollBtns = Array.prototype.slice.call(root.querySelectorAll('[data-cal-rollbtn]'));
    var outHead = root.querySelector('[data-cal-head]');
    var outSub = root.querySelector('[data-cal-sub]');
    var outHint = root.querySelector('[data-cal-hint]');
    var planBox = root.querySelector('[data-cal-plan]');
    var planWidth = root.querySelector('[data-cal-plan-width]');
    var planLength = root.querySelector('[data-cal-plan-length]');
    var planArea = root.querySelector('[data-cal-plan-area]');
    var planOrientation = root.querySelector('[data-cal-plan-orientation]');
    var planBack = root.querySelector('[data-cal-plan-back]');
    var planFrontJoins = root.querySelector('[data-cal-plan-front-joins]');
    var planBackJoins = root.querySelector('[data-cal-plan-back-joins]');
    var planPrice = root.querySelector('[data-cal-plan-price]');
    var planReason = root.querySelector('[data-cal-plan-reason]');
    var planAllowance = root.querySelector('[data-cal-plan-allowance]');
    var planSaveStatus = root.querySelector('[data-cal-plan-status]');
    var planConflict = root.querySelector('[data-cal-plan-conflict]');
    var planConflictText = root.querySelector('[data-cal-plan-conflict-text]');
    var planReplace = root.querySelector('[data-cal-plan-replace]');
    var editMeasurements = root.querySelector('[data-cal-edit]');
    var thickLimit = root.querySelector('[data-cal-thick-limit]');
    var shipNote = root.querySelector('[data-cal-ship]');
    var buyBtn = root.querySelector('[data-cal-buy]');
    var buyLabel = root.querySelector('[data-cal-buylabel]');
    var preorderEl = root.querySelector('[data-cal-preorder]');
    var doneEl = root.querySelector('[data-cal-done]');
    var doneMsg = root.querySelector('[data-cal-donemsg]');
    var errEl = root.querySelector('[data-cal-error]');
    var svg = root.querySelector('[data-cal-svg]');
    var cal3dStage = root.querySelector('[data-cal-3d-stage]');
    var cal3dCanvas = root.querySelector('[data-cal-3d]');
    var threeSrc = root.getAttribute('data-cal-three-src') || '';
    var cal3d = null;
    var lastState = null;
    var backBtns = Array.prototype.slice.call(root.querySelectorAll('[data-cal-backbtn]'));
    var viewBtns = Array.prototype.slice.call(root.querySelectorAll('[data-cal-viewbtn]'));
    var thickSum = root.querySelector('[data-cal-thicksum]');

    var grain = directional ? 'up' : 'auto';
    var backMode = 'partial';
    var rollChoice = 'auto';
    var faceView = 'front';
    var last = null;
    var lastPlan = null;
    var pendingPlan = null;
    var added = false;
    var opened = false;
    var userInteracted = false;
    var planTimer = null;
    var restoredPlan = null;
    var lastTrackedResultId = '';

    function contextualEventData(placement) {
      var plan = readOrderPlan();
      if (plan && plan.handle === handle) return planEventData(plan, { placement: placement });
      return { product_handle: handle, placement: placement };
    }

    function backCfg() { return { mode: backMode }; }

    if (!directional && grainWrap) grainWrap.hidden = true;

    function buyLabelText(text) { if (buyLabel) buyLabel.textContent = text; else if (buyBtn) buyBtn.textContent = text; }

    function syncPresets() {
      var value = num(t);
      presetBtns.forEach(function (b) {
        var on = Math.abs(value - parseFloat(b.getAttribute('data-cal-preset'))) < 1e-9;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function syncRollChoices() {
      rollBtns.forEach(function (b) {
        var value = b.getAttribute('data-cal-rollbtn') || 'auto';
        var on = value === rollChoice;
        var roll = parseInt(value, 10) || 0;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.disabled = roll > 0 && availableRolls.indexOf(roll) === -1;
      });
    }

    function syncChoiceButtons() {
      grainBtns.forEach(function (b) {
        var on = b.getAttribute('data-cal-grainbtn') === grain;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      backBtns.forEach(function (b) {
        var on = b.getAttribute('data-cal-backbtn') === backMode;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      syncRollChoices();
    }

    function markOpened() {
      if (opened) return;
      opened = true;
      trackOrderPlan('calculator_opened', { product_handle: handle, placement: 'product_calculator' });
    }

    function markInteracted() {
      userInteracted = true;
      markOpened();
    }

    function hidePlan() {
      global.clearTimeout(planTimer);
      lastPlan = null;
      if (planBox) planBox.hidden = true;
      if (planReason) planReason.textContent = '';
      if (planAllowance) planAllowance.textContent = '';
    }

    function planJoinLabel(n) {
      n = parseInt(n, 10) || 0;
      if (n === 0) return 'None';
      return n + (n === 1 ? ' join' : ' joins');
    }

    function showPlan(plan) {
      lastPlan = plan;
      if (!plan) { hidePlan(); return; }
      if (planBox) planBox.hidden = false;
      if (planWidth) planWidth.textContent = plan.rollWidthCm + ' cm';
      if (planLength) planLength.textContent = plan.recommendedMetres + ' m';
      if (planArea) planArea.textContent = plan.materialAreaM2 + ' m\u00b2';
      if (planOrientation) planOrientation.textContent = plan.orientation;
      if (planBack) planBack.textContent = plan.backLabel;
      if (planFrontJoins) planFrontJoins.textContent = planJoinLabel(plan.frontJoins);
      if (planBackJoins) planBackJoins.textContent = plan.backMode === 'full' ? planJoinLabel(plan.backJoins) : 'Not needed';
      if (planPrice) planPrice.textContent = 'About ' + money(plan.materialPrice);
      if (planReason) planReason.textContent = plan.reasonText;
      if (planAllowance) planAllowance.textContent = plan.allowanceText;
    }

    function setPlanStatus(text) {
      if (planSaveStatus) planSaveStatus.textContent = text || '';
    }

    function commitPlan(plan, force) {
      if (!plan) return false;
      var active = readOrderPlan();
      if (active && active.handle !== plan.handle && !force) {
        pendingPlan = plan;
        if (planConflictText) planConflictText.textContent = 'You already have a saved plan for ' + active.productName + '.';
        if (planConflict) planConflict.hidden = false;
        setPlanStatus('Your existing plan is still saved.');
        return false;
      }
      pendingPlan = null;
      if (planConflict) planConflict.hidden = true;
      if (active && active.id === plan.id) {
        var measurementsChanged = active.widthCm !== plan.widthCm || active.heightCm !== plan.heightCm || active.thicknessCm !== plan.thicknessCm;
        if (force || measurementsChanged) writeOrderPlan(plan);
        if (measurementsChanged) {
          trackOrderPlan('order_plan_edited', planEventData(plan, { placement: 'product_calculator' }));
        }
        setPlanStatus('Plan saved on this device for 7 days.');
        if (force || measurementsChanged) mountOrderPlanBar();
        return true;
      }
      var eventName = active && active.handle === plan.handle ? 'order_plan_edited' : 'order_plan_saved';
      var replaced = !!(active && active.handle !== plan.handle);
      if (!writeOrderPlan(plan)) {
        setPlanStatus('This browser could not save the plan, but you can still add it to your bag.');
        return false;
      }
      trackOrderPlan(eventName, planEventData(plan, {
        placement: 'product_calculator',
        replaced_existing: replaced
      }));
      setPlanStatus('Plan saved on this device for 7 days.');
      mountOrderPlanBar();
      return true;
    }

    function schedulePlanCommit() {
      if (!userInteracted || !lastPlan) return;
      global.clearTimeout(planTimer);
      planTimer = global.setTimeout(function () {
        if (!lastPlan) return;
        var active = readOrderPlan();
        var resultSignature = [lastPlan.id, lastPlan.widthCm, lastPlan.heightCm, lastPlan.thicknessCm].join('|');
        var activeMatches = active && active.id === lastPlan.id && active.widthCm === lastPlan.widthCm && active.heightCm === lastPlan.heightCm && active.thicknessCm === lastPlan.thicknessCm;
        if (!activeMatches && lastTrackedResultId !== resultSignature) {
          trackOrderPlan('calculator_validated', planEventData(lastPlan, { placement: 'product_calculator' }));
          trackOrderPlan('calculator_result', planEventData(lastPlan, { placement: 'product_calculator' }));
          lastTrackedResultId = resultSignature;
        }
        commitPlan(lastPlan, false);
      }, 300);
    }

    function selectedRolls() {
      if (rollChoice === 'auto') return availableRolls;
      var forced = parseInt(rollChoice, 10) || 0;
      return availableRolls.indexOf(forced) !== -1 ? [forced] : [];
    }

    function showBuy(rec, label) {
      var sz = rollSize(rec.roll);
      var vid = sz ? (parseInt(sz.id, 10) || 0) : 0;
      var avail = sz ? sz.available !== false : false;
      if (doneEl && !added) doneEl.hidden = true;
      var isPre = !!(vid && avail && sz.preorder === true);
      if (preorderEl) {
        var pc = global.VELVEN_PREORDER || {};
        preorderEl.hidden = !isPre;
        preorderEl.textContent = isPre ? ((pc.note || 'Ships in about 8 weeks') + '. ' + (pc.detail || '')).trim() : '';
        if (global.VelvenPreorderArrival) global.VelvenPreorderArrival.render(preorderEl, isPre ? handle : '');
      }
      if (added) return; // keep the confirmation until they change something
      if (isPre) label = 'Preorder ' + rec.roll + 'cm x ' + rec.buyM + 'm roll';
      if (vid && avail) {
        buyBtn.hidden = false;
        buyBtn.disabled = false;
        buyBtn.setAttribute('data-variant', String(vid));
        buyBtn.setAttribute('data-qty', String(rec.buyM));
        buyLabelText(label);
      } else {
        // No matching roll variant; let the customer pick a size by hand instead.
        buyBtn.hidden = true;
        buyBtn.removeAttribute('data-variant');
      }
    }

    function render() {
      if (preorderEl) {
        preorderEl.hidden = true;
        preorderEl.textContent = '';
        if (global.VelvenPreorderArrival) global.VelvenPreorderArrival.render(preorderEl, '');
      }
      var W = num(w), H = num(h), T = num(t);
      var tooThick = T > MAX_PANEL_THICKNESS;
      var visualT = Math.min(T, MAX_PANEL_THICKNESS);
      if (thickSum) thickSum.textContent = (T ? T : 0) + ' cm';
      if (t) t.setAttribute('aria-invalid', tooThick ? 'true' : 'false');
      if (thickLimit) thickLimit.hidden = !tooThick;
      syncPresets();
      syncChoiceButtons();

      var view = panelView({ W: W, H: H, T: visualT, grain: grain, directional: directional, back: backCfg() });
      var candidateRolls = selectedRolls();
      var rec = (W > 0 && H > 0) ? recommend({ W: W, H: H, T: T, grain: grain, prices: prices, availableRolls: candidateRolls, back: backCfg() }) : null;
      paintPanel(svg, view, {
        W: W, H: H, T: visualT, stripGrain: rec ? rec.grain : grain,
        frontSeamFractions: svgSeamFractions(rec, W, H, false),
        backSeamFractions: svgSeamFractions(rec, W, H, true)
      }, faceView);
      lastState = {
        W: W, H: H, T: visualT, grain: grain, roll: rec ? rec.roll : 0,
        strips: rec ? rec.strips : 1, stripGrain: rec ? rec.grain : grain,
        backMode: backMode, pieceW: rec ? rec.pieceW : W, pieceH: rec ? rec.pieceH : H,
        fillW: rec ? rec.fillW : 0, fillH: rec ? rec.fillH : 0, fillStrips: rec ? rec.fillStrips : 0,
        edgeSeamPositions: rec ? rec.edgeCutPlan.seamPositions : [],
        fillSeamPositions: rec ? rec.fillCutPlan.seamPositions : []
      };
      if (cal3d) cal3d.update(lastState);

      if (tooThick) {
        if (outHead) outHead.textContent = 'This thickness is outside the calculator\'s range.';
        if (outSub) outSub.textContent = '';
        if (outHint) { outHint.textContent = ''; outHint.hidden = true; }
        if (shipNote) shipNote.hidden = true;
        if (buyBtn) {
          buyBtn.hidden = true;
          buyBtn.disabled = true;
          buyBtn.removeAttribute('data-variant');
          buyBtn.removeAttribute('data-qty');
        }
        last = null;
        hidePlan();
        return;
      }

      if (W === 0 || H === 0) {
        if (outHead) outHead.textContent = 'Add your width and height and we will size it for you.';
        if (outSub) outSub.textContent = '';
        if (outHint) { outHint.textContent = ''; outHint.hidden = true; }
        if (shipNote) shipNote.hidden = true;
        if (buyBtn) buyBtn.hidden = true;
        last = null;
        hidePlan();
        return;
      }

      if (!rec) {
        if (!candidateRolls.length && preorderEl && global.VelvenPreorderArrival) {
          preorderEl.hidden = !global.VelvenPreorderArrival.render(preorderEl, handle);
        }
        if (outHead) outHead.textContent = 'That finish has no roll size available right now.';
        if (outSub) outSub.textContent = 'Choose a size manually or contact us and we will help plan the project.';
        if (outHint) { outHint.textContent = ''; outHint.hidden = true; }
        if (shipNote) shipNote.hidden = true;
        if (buyBtn) buyBtn.hidden = true;
        last = null;
        hidePlan();
        return;
      }

      last = rec;

      var display = recommendationDisplay(rec, rollChoice === 'auto');
      if (outHead) outHead.textContent = display.heading;
      if (outSub) outSub.textContent = display.summary;
      if (shipNote) shipNote.hidden = !display.showOversizedShipping;

      if (outHint) {
        var hint = '';
        if (directional) {
          var other = grain === 'up' ? 'across' : 'up';
          var otherRec = recommend({ W: W, H: H, T: T, grain: other, prices: prices, availableRolls: candidateRolls, back: backCfg() });
          hint = grainHint(rec, other, otherRec);
        }
        outHint.textContent = hint;
        outHint.hidden = hint === '';
      }

      var chosenSize = rollSize(rec.roll);
      var chosenVariant = chosenSize ? (parseInt(chosenSize.id, 10) || 0) : 0;
      showPlan(createOrderPlan({
        handle: handle,
        productName: mat && mat.name,
        productUrl: mat && mat.url,
        variantId: chosenVariant,
        W: W,
        H: H,
        T: T,
        grain: grain,
        directional: directional,
        backMode: backMode,
        rollChoice: rollChoice,
        rec: rec
      }));

      if (buyBtn) showBuy(rec, display.buyLabel);
      schedulePlanCommit();
    }

    function resetAdded() {
      if (!added) return;
      added = false;
      if (doneEl) doneEl.hidden = true;
      if (errEl) errEl.textContent = '';
    }

    function setFaceView(nextView, snap3d) {
      faceView = nextView === 'back' ? 'back' : 'front';
      viewBtns.forEach(function (b) {
        var on = b.getAttribute('data-cal-viewbtn') === faceView;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (snap3d && cal3d) cal3d.snap(faceView);
    }

    restoredPlan = readOrderPlan();
    if (restoredPlan && restoredPlan.handle === handle) {
      if (w) w.value = restoredPlan.widthCm;
      if (h) h.value = restoredPlan.heightCm;
      if (t) t.value = restoredPlan.thicknessCm;
      grain = restoredPlan.grain === 'across' ? 'across' : (directional ? 'up' : 'auto');
      backMode = restoredPlan.backMode === 'full' ? 'full' : 'partial';
      rollChoice = restoredPlan.rollChoice || 'auto';
      if (rollChoice !== 'auto' && availableRolls.indexOf(parseInt(rollChoice, 10)) === -1) rollChoice = 'auto';
      setPlanStatus('Plan restored from this device.');
    } else {
      restoredPlan = null;
    }

    root.addEventListener('focusin', markOpened);
    root.addEventListener('pointerdown', markOpened);

    var purchasePanel = root.closest ? root.closest('.vmcm-panel--calc') : null;
    var sampleAlternative = root.querySelector('[data-cal-plan] [data-handle]');
    var tutorialLink = purchasePanel ? purchasePanel.querySelector('.vmcm-tutlink__cta') : null;
    var reviewLink = global.document.querySelector('.vmcm-rev-stars[href="#shopify-product-reviews"]');
    var reviewsPanel = global.document.querySelector('#shopify-product-reviews');
    var reviewTracked = false;
    function trackReviewOpen(placement) {
      if (reviewTracked) return;
      reviewTracked = true;
      if (reviewsPanel) reviewsPanel.setAttribute('data-vmcm-review-tracked', '');
      trackOrderPlan('review_opened', contextualEventData(placement));
    }
    if (sampleAlternative) {
      sampleAlternative.addEventListener('click', function () {
        trackOrderPlan('calculator_sample_path_selected', contextualEventData('calculator_result'));
      });
    }
    if (tutorialLink) {
      tutorialLink.addEventListener('click', function () {
        trackOrderPlan('tutorial_opened', contextualEventData('product_calculator'));
      });
    }
    if (reviewLink) {
      reviewLink.addEventListener('click', function () {
        trackReviewOpen('product_hero');
      });
    }
    if (reviewsPanel && global.IntersectionObserver) {
      var reviewObserver = new global.IntersectionObserver(function (entries) {
        for (var reviewIndex = 0; reviewIndex < entries.length; reviewIndex++) {
          if (entries[reviewIndex].isIntersecting) {
            reviewObserver.disconnect();
            trackReviewOpen('review_panel');
            break;
          }
        }
      }, { threshold: 0.3 });
      reviewObserver.observe(reviewsPanel);
    }

    if (editMeasurements) {
      editMeasurements.addEventListener('click', function () {
        markOpened();
        if (w) {
          try { w.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { w.scrollIntoView(); }
          try { w.focus({ preventScroll: true }); } catch (e2) { w.focus(); }
        }
      });
    }

    if (planReplace) {
      planReplace.addEventListener('click', function () {
        markOpened();
        if (pendingPlan) commitPlan(pendingPlan, true);
      });
    }

    grainBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        markInteracted();
        grain = btn.getAttribute('data-cal-grainbtn');
        grainBtns.forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        resetAdded();
        render();
      });
    });

    presetBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        markInteracted();
        if (t) t.value = btn.getAttribute('data-cal-preset');
        presetBtns.forEach(function (b) { b.classList.toggle('is-on', b === btn); });
        resetAdded();
        render();
      });
    });

    rollBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        markInteracted();
        var previousWidth = rollChoice;
        rollChoice = btn.getAttribute('data-cal-rollbtn') || 'auto';
        if (lastPlan && previousWidth !== rollChoice) {
          trackOrderPlan('calculator_recommendation_changed', { product_handle: handle, placement: 'product_calculator', previous_width: previousWidth, roll_width_cm: rollChoice });
        }
        syncRollChoices();
        resetAdded();
        render();
      });
    });

    backBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        markInteracted();
        backMode = btn.getAttribute('data-cal-backbtn') === 'full' ? 'full' : 'partial';
        backBtns.forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        setFaceView('back', true);
        resetAdded();
        render();
      });
    });

    // Front / Back view only changes the diagram, not the recommendation or the
    // cart, so it does not reset the added confirmation.
    viewBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        markOpened();
        setFaceView(btn.getAttribute('data-cal-viewbtn'), true);
        render();
      });
    });

    var inputStarted = false;
    [w, h, t].forEach(function (el) { if (el) el.addEventListener('input', function () {
      markInteracted();
      if (!inputStarted) {
        inputStarted = true;
        trackOrderPlan('calculator_input_started', { product_handle: handle, placement: 'product_calculator', field_category: 'dimensions' });
      }
      resetAdded(); render();
    }); });

    // Cart safe: the ONLY cart write is this explicit tap. No form submit, no
    // auto add, no line mutation. Quantity is the recommended metres because the
    // roll variant is sold per metre ("60 cm x 1 m" / "90 cm x 1 m" / "120 cm x 1 m").
    function planCartProperties(plan) {
      return {
        '_velven_plan_id': plan.id,
        '_velven_plan_schema': String(plan.schema),
        '_velven_plan_experiment': plan.experiment,
        '_velven_plan_roll_width': String(plan.rollWidthCm),
        '_velven_plan_recommended_metres': String(plan.recommendedMetres),
        '_velven_plan_orientation': plan.orientation,
        '_velven_plan_back': plan.backLabel,
        '_velven_plan_back_mode': plan.backMode,
        '_velven_plan_front_joins': String(plan.frontJoins),
        '_velven_plan_back_joins': String(plan.backJoins),
        '_velven_plan_allowance': 'edges_trim_overlap',
        '_velven_plan_reason': plan.reasonCode
      };
    }

    function addToCart(vid, qty) {
      var plan = lastPlan;
      if (!plan) return;
      var items = [{ id: vid, quantity: qty, properties: planCartProperties(plan) }];
      commitPlan(plan, true);
      buyBtn.disabled = true;
      buyBtn.setAttribute('aria-busy', 'true');
      if (errEl) errEl.textContent = '';
      var request = function () {
        return global.fetch(addUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        });
      };
      var mutation = global.VelvenCartEvents
        ? global.VelvenCartEvents.add({ target: buyBtn, context: 'product', items: items }, request)
        : request();
      mutation.then(function () {
        added = true;
        trackOrderPlan('configured_add_to_cart', planEventData(plan, {
          placement: 'product_calculator',
          selected_metres: qty
        }));
        buyBtn.removeAttribute('aria-busy');
        if (typeof global.CustomEvent === 'function') {
          global.document.dispatchEvent(new global.CustomEvent('cart:refresh'));
        }
        if (buyBtn) buyBtn.hidden = true;
        if (doneMsg) doneMsg.textContent = 'Added ' + qty + ' m to your bag.';
        if (doneEl) {
          doneEl.hidden = false;
          // keep keyboard users in flow; the role=status live region announces it
          if (doneEl.focus) { try { doneEl.focus(); } catch (e) {} }
        }
      }).catch(function () {
        buyBtn.disabled = false;
        buyBtn.removeAttribute('aria-busy');
        if (errEl) errEl.textContent = 'Something slipped. Try once more.';
      });
    }

    if (buyBtn) {
      buyBtn.addEventListener('click', function () {
        if (!last) return;
        markInteracted();
        var vid = parseInt(buyBtn.getAttribute('data-variant'), 10) || 0;
        var qty = parseInt(buyBtn.getAttribute('data-qty'), 10) || 1;
        if (!vid) { if (errEl) errEl.textContent = 'That size is not available right now.'; return; }
        addToCart(vid, qty);
      });
    }

    render();
    if (restoredPlan && lastPlan) {
      var requestedPlan = '';
      try { requestedPlan = new global.URLSearchParams(global.location.search).get('vmcm_plan') || ''; } catch (e) {}
      trackOrderPlan('order_plan_restored', planEventData(lastPlan, {
        placement: 'product_calculator',
        restored_via: requestedPlan === restoredPlan.id ? 'resume_link' : 'same_product'
      }));
      mountOrderPlanBar();
    }

    // Progressive enhancement: lazy-load three.js when the calculator nears the
    // viewport and upgrade the SVG diagram to the interactive 3D panel. The SVG is
    // the instant / no-JS / load-failure fallback, so this only ever adds.
    function mount3D() {
      if (cal3d || !cal3dCanvas || !threeSrc) return;
      if (!global.VmcmCal3D) { global.setTimeout(mount3D, 200); return; }
      global.VmcmCal3D.load(threeSrc).then(function () {
        if (cal3d) return;
        cal3d = global.VmcmCal3D.mount(cal3dCanvas, { textureUrl: mat && mat.image });
        if (!cal3d) return;
        if (cal3dStage) cal3dStage.hidden = false;
        if (svg) svg.style.display = 'none';
        if (lastState) cal3d.update(lastState);
        cal3d.nudge();
        if (faceView === 'back') cal3d.snap('back');
        if (root._vmcmFit) root._vmcmFit();
      }, function () { /* three.js failed to load; keep the SVG fallback */ });
    }
    if (global.IntersectionObserver && cal3dCanvas && threeSrc) {
      var io3d = new global.IntersectionObserver(function (entries) {
        for (var ei = 0; ei < entries.length; ei++) {
          if (entries[ei].isIntersecting) { io3d.disconnect(); mount3D(); break; }
        }
      }, { rootMargin: '600px 0px' });
      io3d.observe(root);
    }

    // Keep the calculator at its natural size. The panel is allowed to grow past
    // one viewport so instructions, form controls and result copy stay readable.
    function fit() {
      root.style.transform = 'none';
      root.style.transformOrigin = 'top center';
    }
    root._vmcmFit = fit;
    fit();
    global.requestAnimationFrame(fit);
    if (!global._vmcmCalFitBound) {
      global._vmcmCalFitBound = true;
      var rt;
      var refit = function () {
        var cals = global.document.querySelectorAll('[data-vmcm-cal]');
        for (var i = 0; i < cals.length; i++) if (cals[i]._vmcmFit) cals[i]._vmcmFit();
      };
      global.addEventListener('resize', function () { clearTimeout(rt); rt = global.setTimeout(refit, 120); });
      global.addEventListener('orientationchange', refit);
    }
  }

  function boot() {
    var nodes = global.document.querySelectorAll('[data-vmcm-cal]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
    mountOrderPlanBar();
  }

  if (global.document.readyState !== 'loading') boot();
  else global.document.addEventListener('DOMContentLoaded', boot);
  global.document.addEventListener('shopify:section:load', function (e) {
    var r = e.target && e.target.querySelector ? e.target.querySelector('[data-vmcm-cal]') : null;
    if (r) init(r);
  });
})(typeof self !== 'undefined' ? self : this);
